<template>
  <div class="page-container">
    <el-card class="card">
      <template #header>
        <div class="card-header">
          <span>固件版本管理</span>
          <el-button type="primary" size="small" @click="onCreate">新增固件版本</el-button>
        </div>
      </template>

      <el-form :inline="true" size="small" class="filter-form" @submit.prevent>
        <el-form-item label="版型">
          <el-select
            v-model="filters.boardType"
            placeholder="选择硬件版型"
            clearable
            style="min-width: 200px;"
          >
            <el-option
              v-for="bt in boardTypes"
              :key="bt.id"
              :label="bt.name"
              :value="String(bt.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="仅显示启用">
          <el-switch v-model="filters.activeOnly" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchData">查询</el-button>
        </el-form-item>
      </el-form>

      <el-table :data="firmwares" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="board_type" label="版型" width="120">
          <template #default="scope">
            {{ getBoardTypeName(scope.row.board_type) }}
          </template>
        </el-table-column>
        <el-table-column prop="version" label="版本号" width="120" />
        <el-table-column prop="build_code" label="build_code" width="100" />
        <el-table-column prop="is_active" label="启用" width="80">
          <template #default="scope">
            <el-tag :type="scope.row.is_active ? 'success' : 'info'">
              {{ scope.row.is_active ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="is_force" label="强制" width="80">
          <template #default="scope">
            <el-tag :type="scope.row.is_force ? 'danger' : 'info'">
              {{ scope.row.is_force ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="rollout_percentage" label="放量" width="80">
          <template #default="scope">
            {{ (scope.row.rollout_percentage ?? 100) }}%
          </template>
        </el-table-column>
        <el-table-column prop="download_url" label="下载地址" min-width="200" show-overflow-tooltip />
        <el-table-column prop="description" label="说明" min-width="200" show-overflow-tooltip />
        <el-table-column prop="created_at" label="创建时间" width="170" />
        <el-table-column label="操作" width="280" fixed="right">
          <template #default="scope">
            <el-button size="small" @click="openRolloutDialog(scope.row)">覆盖设备</el-button>
            <el-button size="small" type="primary" @click="onEdit(scope.row)">编辑</el-button>
            <el-button
              size="small"
              type="danger"
              @click="onDelete(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      v-model="dialog.visible"
      :title="dialog.isEdit ? '编辑固件版本' : '新增固件版本'"
      width="600px"
    >
      <el-form :model="dialog.form" label-width="140px">
        <el-form-item label="版型" required>
          <el-select
            v-model="dialog.form.board_type"
            placeholder="请选择硬件版型"
            style="width: 260px;"
            :disabled="dialog.isEdit"
          >
            <el-option
              v-for="bt in boardTypes"
              :key="bt.id"
              :label="bt.name"
              :value="String(bt.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="版本号" required>
          <el-input
            v-model="dialog.form.version"
            placeholder="例如 1.0.5"
            :disabled="dialog.isEdit"
          />
        </el-form-item>
        <el-form-item label="build_code" required>
          <el-input-number v-model="dialog.form.build_code" :min="1" :disabled="true" />
        </el-form-item>
        <el-form-item label="固件文件（七牛）">
          <div class="upload-row">
            <el-upload
              :auto-upload="false"
              :file-list="uploadFileList"
              :limit="1"
              :on-change="onFirmwareFileChange"
              :on-remove="onFirmwareFileRemove"
              accept=".bin,.zip"
            >
              <el-button size="small" type="primary">选择固件文件</el-button>
            </el-upload>
            <el-button
              size="small"
              type="success"
              :disabled="!firmwareFile"
              :loading="uploading"
              @click="onUploadFirmware"
              style="margin-left: 12px;"
            >
              上传到七牛
            </el-button>
          </div>
          <div class="upload-tip">
            选择 BIN 或 ZIP 固件文件，点击「上传到七牛」后，会自动生成 CDN 下载地址和文件大小、哈希。
          </div>
        </el-form-item>
        <el-form-item label="下载地址" required>
          <el-input
            v-model="dialog.form.download_url"
            placeholder="上传到七牛后自动生成，无需填写"
            readonly
            disabled
          />
        </el-form-item>
        <el-form-item label="文件大小（字节）">
          <el-input-number v-model="dialog.form.file_size_bytes" :min="0" />
        </el-form-item>
        <el-form-item label="哈希">
          <el-input
            v-model="dialog.form.hash"
            placeholder="上传固件后自动生成，无需填写"
            readonly
            disabled
          />
        </el-form-item>
        <el-form-item label="哈希算法">
          <el-input
            v-model="dialog.form.hash_algo"
            placeholder="自动填充，通常为 sha256"
            readonly
            disabled
          />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="dialog.form.is_active" />
        </el-form-item>
        <el-form-item label="强制升级">
          <el-switch v-model="dialog.form.is_force" />
        </el-form-item>
        <el-form-item label="最小来源 build">
          <el-input-number v-model="dialog.form.min_build_code" :min="0" />
          <div class="field-tip">
            留空表示任何更低版本都可以直接升级到当前固件。
            只有在需要“先升级到某个过渡版本，再升级到当前版本”时，才填写允许直升的最低 build_code。
          </div>
        </el-form-item>
        <el-form-item label="放量比例（%）">
          <el-input-number
            v-model="dialog.form.rollout_percentage"
            :min="1"
            :max="100"
            placeholder="100=全量"
          />
          <div class="field-tip">1–100，100 表示全量；小于 100 时仅部分设备会收到该版本，可逐步调大放量。</div>
        </el-form-item>
        <el-form-item label="说明">
          <el-input
            v-model="dialog.form.description"
            type="textarea"
            :rows="3"
            placeholder="更新说明 / 适配说明"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" @click="onSave" :loading="dialog.saving">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="rolloutDialog.visible"
      :title="rolloutDialog.title"
      width="700px"
      @opened="fetchRolloutDevices"
    >
      <div v-loading="rolloutDialog.loading">
        <p class="rollout-tip">以下设备在当前放量比例下会收到该固件（仅统计管理员与用户均允许 OTA 的设备）。</p>
        <el-table :data="rolloutDialog.list" max-height="400">
          <el-table-column prop="device_id" label="设备ID" min-width="140" show-overflow-tooltip />
          <el-table-column prop="name" label="名称" width="120" show-overflow-tooltip />
          <el-table-column prop="firmware_version" label="当前版本" width="100" />
          <el-table-column prop="status" label="状态" width="80">
            <template #default="scope">
              <span v-if="scope.row.status === 'active'">已激活</span>
              <span v-else-if="scope.row.status === 'inactive'">未激活</span>
              <span v-else>{{ scope.row.status || '-' }}</span>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-model:current-page="rolloutDialog.page"
          :page-size="rolloutDialog.pageSize"
          :total="rolloutDialog.total"
          layout="total, prev, pager, next"
          style="margin-top: 12px;"
          @current-change="fetchRolloutDevices"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { ElMessageBox, ElMessage } from 'element-plus';
import {
  listFirmwares,
  createFirmware,
  updateFirmware,
  deleteFirmware,
  uploadFirmwareFile,
  getRolloutDevices,
} from '@/api/firmware';
import { getBoardTypes } from '../api/device';

const loading = ref(false);
const firmwares = ref([]);
const boardTypes = ref([]);

const getBoardTypeName = (id) => {
  if (!id && id !== 0) return '';
  const found = boardTypes.value.find((bt) => String(bt.id) === String(id));
  return found ? found.name : String(id ?? '');
};

const filters = reactive({
  boardType: '',
  activeOnly: false,
});

const dialog = reactive({
  visible: false,
  isEdit: false,
  saving: false,
  form: {
    id: null,
    board_type: '',
    version: '',
    build_code: null,
    download_url: '',
    file_size_bytes: null,
    hash: '',
    hash_algo: '',
    is_active: true,
    is_force: false,
    min_build_code: null,
    description: '',
    rollout_percentage: 100,
  },
});

const rolloutDialog = reactive({
  visible: false,
  title: '覆盖设备',
  firmwareId: null,
  loading: false,
  list: [],
  total: 0,
  page: 1,
  pageSize: 20,
});

const firmwareFile = ref(null);
const uploadFileList = ref([]);
const uploading = ref(false);

const computeBuildCodeFromVersion = (version) => {
  if (!version) return null;
  const parts = String(version).trim().split('.');
  const major = parseInt(parts[0] || '0', 10);
  const minor = parseInt(parts[1] || '0', 10);
  const patch = parseInt(parts[2] || '0', 10);
  if (Number.isNaN(major) || Number.isNaN(minor) || Number.isNaN(patch)) {
    return null;
  }
  return major * 10000 + minor * 100 + patch;
};

watch(
  () => dialog.form.version,
  (val) => {
    const code = computeBuildCodeFromVersion(val);
    dialog.form.build_code = code;
  }
);

const fetchData = async () => {
  loading.value = true;
  try {
    const resp = await listFirmwares({
      board_type: filters.boardType || undefined,
      active_only: filters.activeOnly ? 'true' : 'false',
    });
    if (resp.success) {
      firmwares.value = resp.data || [];
    } else {
      ElMessage.error(resp.message || '获取固件列表失败');
    }
  } catch (e) {
    ElMessage.error('获取固件列表失败');
  } finally {
    loading.value = false;
  }
};

const fetchBoardTypes = async () => {
  try {
    const res = await getBoardTypes({ all: true });
    if (res && res.data) {
      boardTypes.value = res.data;
    }
  } catch {
    // 忽略错误，版型列表为空时仍可手动输入
  }
};

const resetForm = () => {
  dialog.form = {
    id: null,
    board_type: '',
    version: '',
    build_code: null,
    download_url: '',
    file_size_bytes: null,
    hash: '',
    hash_algo: '',
    is_active: true,
    is_force: false,
    min_build_code: null,
    description: '',
    rollout_percentage: 100,
  };
  firmwareFile.value = null;
  uploadFileList.value = [];
};

const onCreate = () => {
  resetForm();
  dialog.isEdit = false;
  dialog.visible = true;
};

const onEdit = (row) => {
  dialog.isEdit = true;
  dialog.visible = true;
  dialog.form = {
    id: row.id,
    board_type: row.board_type,
    version: row.version,
    build_code: row.build_code,
    download_url: row.download_url,
    file_size_bytes: row.file_size_bytes,
    hash: row.hash,
    hash_algo: row.hash_algo,
    is_active: row.is_active,
    is_force: row.is_force,
    min_build_code: row.min_build_code,
    description: row.description,
    rollout_percentage: row.rollout_percentage ?? 100,
  };
  firmwareFile.value = null;
  uploadFileList.value = [];
};

const onFirmwareFileChange = (file, fileList) => {
  firmwareFile.value = file.raw || null;
  uploadFileList.value = fileList.slice(-1);
};

const onFirmwareFileRemove = () => {
  firmwareFile.value = null;
  uploadFileList.value = [];
};

const onUploadFirmware = async () => {
  if (!firmwareFile.value) {
    ElMessage.warning('请先选择固件文件');
    return;
  }
  uploading.value = true;
  try {
    const resp = await uploadFirmwareFile(firmwareFile.value);
    if (resp && resp.success) {
      const data = resp.data || {};
      if (data.download_url) {
        dialog.form.download_url = data.download_url;
      }
      if (data.file_size_bytes != null) {
        dialog.form.file_size_bytes = data.file_size_bytes;
      }
      if (data.hash) {
        dialog.form.hash = data.hash;
      }
      if (data.hash_algo) {
        dialog.form.hash_algo = data.hash_algo;
      }
      ElMessage.success('固件上传成功');
    } else {
      ElMessage.error(resp?.message || '固件上传失败');
    }
  } catch (e) {
    const msg = e?.response?.data?.message || e.message || '固件上传失败';
    ElMessage.error(msg);
  } finally {
    uploading.value = false;
  }
};

const onSave = async () => {
  dialog.saving = true;
  try {
    const payload = { ...dialog.form };
    if (!dialog.isEdit) {
      delete payload.id;
      const resp = await createFirmware(payload);
      if (resp.success) {
        ElMessage.success('创建成功');
        dialog.visible = false;
        await fetchData();
      } else {
        ElMessage.error(resp.message || '创建失败');
      }
    } else {
      const id = dialog.form.id;
      const resp = await updateFirmware(id, payload);
      if (resp.success) {
        ElMessage.success('更新成功');
        dialog.visible = false;
        await fetchData();
      } else {
        ElMessage.error(resp.message || '更新失败');
      }
    }
  } catch (e) {
    ElMessage.error('保存失败');
  } finally {
    dialog.saving = false;
  }
};

const onDelete = async (row) => {
  try {
    await ElMessageBox.confirm(`确定要删除固件版本 ${row.version} (board_type=${row.board_type}) 吗？`, '提示', {
      type: 'warning',
    });
  } catch {
    return;
  }
  try {
    const resp = await deleteFirmware(row.id);
    if (resp.success) {
      ElMessage.success('删除成功');
      await fetchData();
    } else {
      ElMessage.error(resp.message || '删除失败');
    }
  } catch (e) {
    ElMessage.error('删除失败');
  }
};

const openRolloutDialog = (row) => {
  rolloutDialog.firmwareId = row.id;
  rolloutDialog.title = `覆盖设备：${getBoardTypeName(row.board_type)} ${row.version}（放量 ${row.rollout_percentage ?? 100}%）`;
  rolloutDialog.list = [];
  rolloutDialog.total = 0;
  rolloutDialog.page = 1;
  rolloutDialog.visible = true;
};

const fetchRolloutDevices = async () => {
  if (!rolloutDialog.firmwareId || !rolloutDialog.visible) return;
  rolloutDialog.loading = true;
  try {
    const resp = await getRolloutDevices(rolloutDialog.firmwareId, {
      page: rolloutDialog.page,
      page_size: rolloutDialog.pageSize,
      ota_enabled_only: 'true',
    });
    if (resp && resp.success) {
      rolloutDialog.list = resp.data || [];
      rolloutDialog.total = resp.total ?? 0;
    } else {
      rolloutDialog.list = [];
      rolloutDialog.total = 0;
    }
  } catch {
    rolloutDialog.list = [];
    rolloutDialog.total = 0;
  } finally {
    rolloutDialog.loading = false;
  }
};

onMounted(() => {
  fetchBoardTypes();
  fetchData();
});
</script>

<style scoped>
.page-container {
  padding: 20px;
}
.card {
  max-width: 1200px;
  margin: 0 auto;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.filter-form {
  margin-bottom: 16px;
}
.upload-row {
  display: flex;
  align-items: center;
}
.upload-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.field-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.rollout-tip {
  font-size: 12px;
  color: #606266;
  margin-bottom: 12px;
}
</style>

